import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Ellipsis, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { label, lifespan, RELATION_NAMES, relationsFor, type Relation } from '../../shared/tree'
import { ActionMenu } from '@/components/ActionMenu'
import { AddFor } from '@/components/AddFor'
import { ConfirmButton } from '@/components/ConfirmButton'
import { FormError } from '@/components/FormError'
import { MomentCard } from '@/components/MomentCard'
import { PhotoWall } from '@/components/modern/ModernAlbum'
import { MomentWall } from '@/components/modern-moments/MomentWall'
import { MomentsIcon } from '@/components/nav/icons'
import { InviteForm } from '@/components/people/InviteForm'
import { PersonSelect } from '@/components/PersonSelect'
import { FromWall } from '@/components/recipes/FromWall'
import { RecipeTile } from '@/components/RecipeTile'
import { PhotoGrid } from '@/components/PhotoGrid'
import { Pills } from '@/components/Pills'
import { Remembrances } from '@/components/Remembrances'
import { Avatar } from '@/components/tree/Avatar'
import { PersonForm } from '@/components/tree/PersonForm'
import { PortraitButton } from '@/components/tree/PortraitButton'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { api } from '@/lib/api'
import { useFamily } from '@/lib/families'
import { circleSections } from '@/lib/sections'
import { usePageFrame } from '@/lib/frame'
import { paragraphs } from '@/lib/moments'
import { NO_BOOK } from '@/lib/recipes'
import { usePerson, useTree, type MemorialFields, type Person, type PersonFields } from '@/lib/tree'
import { useMe } from '@/lib/useMe'
import { PageLoader } from '@/components/Loader'

// A person's page, where tapping the middle of the orbit leads, or their name on a photo: who they are, who keeps the
// page, their people, the recipes that came from them, the photos and moments you share with them, and the photos
// they're in. For someone who has passed it's their memorial page: the line the family remembers them by, their story
// (written by whoever keeps the page), what everyone loved most about them, and then their recipes, photos and
// memories, with nothing filled in that the family hasn't written.
export function PersonPage() {
  const { id = '' } = useParams()
  const person = usePerson(id)
  if (person.isPending) return <PageLoader />
  if (person.isError) return <FormError error={person.error} />
  return <PersonView person={person.data} />
}

type Kind = 'photos' | 'moments' | 'recipes'

function PersonView({ person }: { person: Person }) {
  const me = useMe().data
  const family = useFamily(person.familyId).data
  const circle = family?.name
  // What this person's circle shows, under the owner's switches.
  const sections = me && family ? circleSections(me.sections, family) : undefined
  // Each kind of thing on the page follows its own section's style, as the owner set it.
  const modernPhotos = me?.photosStyle === 'modern'
  const modernMoments = me?.momentsStyle === 'modern'
  const modernRecipes = me?.recipesStyle === 'modern'
  const queryClient = useQueryClient()
  const first = person.name
  const memorial = !person.living
  const [editing, setEditing] = useState<false | 'details' | 'story'>(false)
  const [showing, setShowing] = useState<'all' | Kind>('all')
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['person'] })
    await queryClient.invalidateQueries({ queryKey: ['tree', person.familyId] })
    // Their face shows on the family page, and on the account linked to them.
    await queryClient.invalidateQueries({ queryKey: ['family', person.familyId] })
    await queryClient.invalidateQueries({ queryKey: ['me'] })
  }
  usePageFrame(person.familyId, {
    title: person.name,
    back: { to: `/circle/${person.familyId}/people/${person.id}`, label: `Back to ${first}’s people` },
  })
  const save = useMutation({
    mutationFn: (fields: PersonFields & Partial<MemorialFields>) =>
      api(`/people/${person.id}`, { method: 'PATCH', body: fields }),
    onSuccess: async () => {
      setEditing(false)
      await refresh()
    },
  })
  const years = lifespan(person)
  const tools = person.canEdit && !editing && (
    <div className="flex flex-wrap justify-center gap-2 pt-1">
      <Button variant="outline" size="sm" onClick={() => setEditing('details')}>
        Edit
      </Button>
      <PortraitButton person={person} onDone={refresh} />
    </div>
  )

  const people = (
    <section className="flex flex-col gap-4" aria-labelledby="their-people">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="their-people" className="text-title">
          {first}’s people
        </h2>
        <Link
          to={`/circle/${person.familyId}/people/${person.id}`}
          className="flex min-h-11 shrink-0 items-center text-sm whitespace-nowrap underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          Show in the tree
        </Link>
      </div>
      {person.connections.length === 0 ? (
        <p className="text-muted-foreground">No one is linked to {first} yet. Add them from the tree.</p>
      ) : (
        <Connections person={person} onDone={refresh} />
      )}
    </section>
  )

  const recipes = sections?.recipes && person.recipes.length > 0 && (
    <section className="flex flex-col gap-6" aria-labelledby="their-recipes">
      <h2 id="their-recipes" className="text-title">
        Recipes from {first}
      </h2>
      {modernRecipes ? (
        <FromWall familyId={person.familyId} personId={person.id} />
      ) : (
        <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6">
          {person.recipes.map((recipe, i) => (
            <li key={recipe.id}>
              <RecipeTile recipe={recipe} index={i} line={recipe.book ?? NO_BOOK} />
            </li>
          ))}
        </ul>
      )}
    </section>
  )

  const together = sections?.photos && person.together.length > 0 && (
    <section className="flex flex-col gap-6" aria-labelledby="photos-together">
      <div className="flex flex-col gap-1">
        <h2 id="photos-together" className="text-title">
          You and {first}
        </h2>
        <p className="text-sub text-muted-foreground">Photos you’re both in.</p>
      </div>
      {modernPhotos ? (
        <PhotoWall
          photos={person.together}
          familyId={person.familyId}
          label={`You and ${first}`}
          moments={!!sections?.moments}
          param="with"
        />
      ) : (
        <PhotoGrid photos={person.together} small />
      )}
    </section>
  )

  // On a memorial page this is where longer memories go, with photos if there are any: a moment that tags them, kept
  // just for its author until they share it. So it shows even before anyone has written one.
  const moments = sections?.moments && (person.moments.length > 0 || memorial) && (
    <section className="flex flex-col gap-6" aria-labelledby="moments-together">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="moments-together" className="text-title">
          {memorial ? `Memories of ${first}` : person.self ? 'Moments you’re in' : `Moments with ${first}`}
        </h2>
        {memorial && person.moments.length > 0 && <WriteMemory person={person} />}
      </div>
      {person.moments.length === 0 ? (
        <div className="flex flex-col items-start gap-3">
          <p className="text-muted-foreground">
            A longer story about {first}, with photos if you have them. It’s kept just for you until you share it.
          </p>
          <WriteMemory person={person} />
        </div>
      ) : modernMoments ? (
        <MomentWall moments={person.moments} />
      ) : (
        <div className="flex flex-col gap-10">
          {person.moments.map((moment) => (
            <MomentCard key={moment.id} moment={moment} headingLevel={3} />
          ))}
        </div>
      )}
      {person.momentCount > person.moments.length && (
        <p className="text-sub text-muted-foreground">
          The {person.moments.length} most recent of {person.momentCount}.
        </p>
      )}
    </section>
  )

  // A memorial page keeps a place for the photos of them even before anyone has tagged them in one.
  const photos = sections?.photos && (person.photos.length > 0 || memorial) && (
    <section className="flex flex-col gap-6" aria-labelledby="their-photos">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="their-photos" className="text-title">
          Photos of {first}
        </h2>
        {person.photoCount > person.photos.length && (
          <Link
            to={`/circle/${person.familyId}/photos?${new URLSearchParams(modernPhotos ? { show: `person:${person.id}` } : { person: person.id })}`}
            className="flex min-h-11 shrink-0 items-center text-sm whitespace-nowrap underline-offset-4 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            All {person.photoCount}
          </Link>
        )}
      </div>
      {person.photos.length === 0 ? (
        <div className="flex flex-col items-start gap-3">
          <p className="text-muted-foreground">
            No photos of {first} yet. Tag {first} in any photo and it shows here.
          </p>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/circle/${person.familyId}/photos`}>Go to Photos</Link>
          </Button>
        </div>
      ) : modernPhotos ? (
        <PhotoWall
          photos={person.photos}
          familyId={person.familyId}
          label={`Photos of ${first}`}
          moments={!!sections?.moments}
          here={{ kind: 'person', id: person.id }}
        />
      ) : (
        <PhotoGrid photos={person.photos} small />
      )}
    </section>
  )

  // Their page organized: everything, or just their photos, moments or recipes, once there's more than one kind.
  const kinds = (
    [
      ['photos', 'Photos', sections?.photos ? person.photoCount : 0],
      ['moments', 'Moments', sections?.moments ? person.momentCount : 0],
      ['recipes', 'Recipes', sections?.recipes ? person.recipes.length : 0],
    ] as [Kind, string, number][]
  ).filter(([, , n]) => n > 0)
  const only = kinds.length > 1 && kinds.some(([k]) => k === showing) ? showing : 'all'
  const organize = kinds.length > 1 && !editing && (
    <Pills
      label={`Show from ${first}’s page`}
      value={only}
      options={[['all', 'Everything'], ...kinds.map(([k, name, n]) => [k, `${name} ${n}`] as [Kind, string])]}
      onChange={setShowing}
      compact
    />
  )
  const picked =
    only === 'photos' ? (
      <>
        {photos}
        {together}
      </>
    ) : only === 'moments' ? (
      moments
    ) : only === 'recipes' ? (
      recipes
    ) : null

  return (
    <article className={memorial ? 'flex flex-col gap-14' : 'flex flex-col gap-10'}>
      {memorial ? (
        <MemorialHeader person={person} years={years}>
          <AddFor person={person} />
          {tools}
        </MemorialHeader>
      ) : (
        <header className="flex flex-col items-center gap-3 text-center">
          <Avatar person={person} variant="display" className="size-36 text-6xl sm:size-44" />
          <h1 className="text-display text-balance">{person.name}</h1>
          {years && <p className="text-muted-foreground">{years}</p>}
          <p className="text-sub text-muted-foreground">{keptBy(person)}</p>
          <AddFor person={person} />
          {tools}
        </header>
      )}

      {editing && (
        <section className="flex flex-col gap-4 rounded-3xl bg-secondary/70 p-5 sm:p-6" aria-label={`Edit ${first}`}>
          <PersonForm
            initial={person}
            pet={person.pet}
            withNote
            focus={editing === 'story' ? 'story' : undefined}
            label="Save"
            pending={save.isPending}
            error={save.error}
            onSave={(f) => save.mutate(f)}
          />
          <Button variant="ghost" className="self-start" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </section>
      )}

      {!editing && person.note && <p className="whitespace-pre-wrap">{person.note}</p>}

      <Requests person={person} onDone={refresh} />
      <Claim person={person} onDone={refresh} />

      {organize && <div className="flex [scrollbar-width:none] justify-center overflow-x-auto">{organize}</div>}

      {picked ? (
        picked
      ) : memorial ? (
        <>
          {!editing && <Story person={person} onWrite={() => setEditing('story')} />}
          <Remembrances person={person} circle={circle ?? 'this circle'} onDone={refresh} />
          {recipes}
          {photos}
          {moments}
          {together}
          {people}
          {person.restingPlace && (
            <section className="flex flex-col gap-2" aria-labelledby="resting-place">
              <h2 id="resting-place" className="text-title">
                Resting place
              </h2>
              <p>{person.restingPlace}</p>
            </section>
          )}
        </>
      ) : (
        <>
          {people}
          {recipes}
          {together}
          {moments}
          {photos}
        </>
      )}

      {person.canEdit && <Keeping person={person} onDone={refresh} />}
      {person.self && <Unlink person={person} onDone={refresh} />}
    </article>
  )
}

// The top of a memorial page: their face, their name and years, and the line the family chose to remember them by.
function MemorialHeader({ person, years, children }: { person: Person; years: string; children: ReactNode }) {
  return (
    <header className="flex flex-col items-center gap-5 pt-2 text-center sm:pt-6">
      <p className="flex items-center gap-3 eyebrow text-muted-foreground">
        <span aria-hidden className="h-px w-8 bg-border" />
        In loving memory
        <span aria-hidden className="h-px w-8 bg-border" />
      </p>
      <Avatar person={person} variant="display" className="size-44 text-7xl sm:size-56" />
      <div className="flex flex-col items-center gap-2">
        <h1 className="text-display text-balance">{person.name}</h1>
        {years && <p className="text-lg text-muted-foreground tabular-nums">{years}</p>}
      </div>
      {person.epitaph && <p className="max-w-md text-xl leading-snug text-balance italic">{person.epitaph}</p>}
      <p className="text-sub text-muted-foreground">{keptBy(person)}</p>
      {children}
    </header>
  )
}

// Their story, in the keeper's words. Until it's written, only the keeper sees the space for it.
function Story({ person, onWrite }: { person: Person; onWrite: () => void }) {
  const first = person.name
  if (!person.story) {
    if (!person.canEdit) return null
    return (
      <section
        className="flex flex-col items-start gap-3 rounded-3xl border border-border p-5 sm:p-6"
        aria-label="Their story"
      >
        <h2 className="text-title">Tell {first}’s story</h2>
        <p className="text-muted-foreground">
          {person.pet
            ? 'Where they came from, their habits, the trouble they got into. Only you can write it here; everyone else adds what they loved most.'
            : 'Where they grew up, the work they did, the people they loved, what they were like. Only you can write it here; everyone else adds what they loved most.'}
        </p>
        <Button variant="outline" onClick={onWrite}>
          Write {first}’s story
        </Button>
      </section>
    )
  }
  return (
    <section className="flex flex-col gap-5" aria-labelledby="their-story">
      <h2 id="their-story" className="text-title">
        {first}’s story
      </h2>
      <div className="flex max-w-prose flex-col gap-4 text-lg leading-relaxed text-pretty">
        {paragraphs(person.story).map((p, i) => (
          <p key={i} className="whitespace-pre-wrap">
            {p}
          </p>
        ))}
      </div>
    </section>
  )
}

// A moment that starts with them already tagged.
function WriteMemory({ person }: { person: Person }) {
  return (
    <Button variant="outline" size="sm" asChild>
      <Link to={`/circle/${person.familyId}/moments/new?${new URLSearchParams({ with: person.id })}`}>
        <MomentsIcon /> Write a memory
      </Link>
    </Button>
  )
}

function keptBy(person: Person) {
  if (person.self) return 'This is you. You keep this page.'
  if (person.linked) return `${person.name} keeps this page.`
  if (!person.living) return `Kept for the family by ${person.keptBy.name}.`
  return `Kept by ${person.keptBy.name}.`
}

function Connections({ person, onDone }: { person: Person; onDone: () => void }) {
  const remove = useMutation({
    mutationFn: (link: string) => api(`/connections/${link}`, { method: 'DELETE' }),
    onSuccess: onDone,
  })
  const change = useMutation({
    mutationFn: ({ link, as }: { link: string; as: Relation }) =>
      api(`/connections/${link}`, { method: 'PATCH', body: { of: person.id, as } }),
    onSuccess: onDone,
  })
  const pending = remove.isPending || change.isPending
  return (
    <>
      <ul className="flex flex-col">
        {person.connections.map((c) => (
          <li key={c.person.id} className="flex items-center gap-1">
            <Link
              to={`/person/${c.person.id}`}
              className="flex min-h-14 min-w-0 flex-1 items-center gap-3 rounded-2xl px-2 outline-none hover:bg-secondary focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <Avatar person={c.person} className="size-10 text-base" />
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{c.person.name}</span>
                <span className="text-sub text-muted-foreground">{label(c.relation, c.person.referAs)}</span>
              </span>
            </Link>
            {c.removable && c.link && (
              // What they are to this person, if it was entered wrong (the dog added as a son), or unlinking them.
              <ActionMenu
                title={`What is ${c.person.name} to ${person.name}?`}
                label={`What is ${c.person.name} to ${person.name}?`}
                align="end"
                trigger={
                  <Button variant="ghost" size="icon" aria-label={`Change ${c.person.name}`} disabled={pending}>
                    <Ellipsis aria-hidden />
                  </Button>
                }
                groups={[
                  relationsFor(person).map((as) => ({
                    label: RELATION_NAMES[as],
                    current: as === c.relation,
                    onSelect: () => as !== c.relation && change.mutate({ link: c.link!, as }),
                  })),
                  [{ label: 'Unlink', icon: X, onSelect: () => remove.mutate(c.link!) }],
                ]}
              />
            )}
          </li>
        ))}
      </ul>
      <FormError error={remove.error ?? change.error} />
    </>
  )
}

// People who signed up and say this is them. Whoever keeps the page decides.
function Requests({ person, onDone }: { person: Person; onDone: () => void }) {
  const decide = useMutation({
    mutationFn: ({ id, approve }: { id: string; approve: boolean }) =>
      api(`/people/${person.id}/requests/${id}`, { method: 'POST', body: { approve } }),
    onSuccess: onDone,
  })
  if (person.requests.length === 0) return null
  return (
    <section className="flex flex-col gap-3 rounded-3xl bg-secondary/70 p-5" aria-label="Asking to be linked">
      {person.requests.map((r) => (
        <div key={r.id} className="flex flex-col gap-3">
          <p>
            <strong className="font-medium">{r.name}</strong> ({r.email}) says this is them. If you say yes, this page
            becomes theirs to keep.
          </p>
          <div className="flex gap-2">
            <Button disabled={decide.isPending} onClick={() => decide.mutate({ id: r.id, approve: true })}>
              Yes, that’s them
            </Button>
            <Button
              variant="outline"
              disabled={decide.isPending}
              onClick={() => decide.mutate({ id: r.id, approve: false })}
            >
              Decline
            </Button>
          </div>
        </div>
      ))}
      <FormError error={decide.error} />
    </section>
  )
}

// "Is this you?" for someone signed in who isn't on the tree yet.
function Claim({ person, onDone }: { person: Person; onDone: () => void }) {
  const claim = useMutation({
    mutationFn: () => api<{ linked?: boolean }>(`/people/${person.id}/claim`, { method: 'POST' }),
    onSuccess: onDone,
  })
  const withdraw = useMutation({
    mutationFn: () => api(`/people/${person.id}/claim`, { method: 'DELETE' }),
    onSuccess: onDone,
  })
  if (person.asked) {
    return (
      <div className="flex flex-col gap-2 rounded-3xl bg-secondary/70 p-5" role="status">
        <p>We’ve asked {person.keptBy.name} to confirm this is you.</p>
        <Button variant="ghost" size="sm" className="self-start" onClick={() => withdraw.mutate()}>
          Take back
        </Button>
      </div>
    )
  }
  if (!person.canClaim) return null
  return (
    <div className="flex flex-col gap-2 rounded-3xl border border-border p-5">
      <p>Is this you? Say so, and this page becomes yours to keep.</p>
      <FormError error={claim.error} />
      <Button variant="outline" className="self-start" disabled={claim.isPending} onClick={() => claim.mutate()}>
        This is me
      </Button>
    </div>
  )
}

// Looking after the page: inviting them (never a pet), handing it to someone else, merging a duplicate, taking it off
// the tree.
function Keeping({ person, onDone }: { person: Person; onDone: () => void }) {
  const canInvite = useMe().data?.canInvite ?? false
  const first = person.name
  return (
    <section className="flex flex-col gap-8 border-t pt-8" aria-labelledby="keeping">
      <h2 id="keeping" className="text-title">
        Looking after this page
      </h2>
      {person.living && !person.pet && !person.linked && canInvite && <InvitePerson person={person} />}
      {!person.linked && <HandOn person={person} onDone={onDone} />}
      {!person.linked && <SamePerson person={person} />}
      {!person.linked && <Remove person={person} first={first} />}
    </section>
  )
}

function Field({ label: text, htmlFor, children }: { label: string; htmlFor: string; children: ReactNode }) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={htmlFor}>{text}</Label>
      {children}
    </div>
  )
}

// Inviting this person: their page becomes theirs if they say "Is this you?" when they join.
function InvitePerson({ person }: { person: Person }) {
  const family = useFamily(person.familyId)
  const [open, setOpen] = useState(false)
  if (!family.data) return null
  return (
    <div className="grid gap-2">
      <p className="text-sm font-medium">Invite {person.name}</p>
      <p className="text-sub text-muted-foreground">
        They’ll be asked “Is this you?” and, if so, this page becomes theirs to keep.
      </p>
      <Button variant="outline" className="self-start" onClick={() => setOpen(true)}>
        Invite {person.name}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Invite {person.name}</DialogTitle>
            <DialogDescription>It works once, for 7 days.</DialogDescription>
          </DialogHeader>
          <InviteForm family={family.data} person={person} />
        </DialogContent>
      </Dialog>
    </div>
  )
}

function HandOn({ person, onDone }: { person: Person; onDone: () => void }) {
  const family = useFamily(person.familyId)
  const [to, setTo] = useState('')
  const hand = useMutation({
    mutationFn: () => api(`/people/${person.id}/keeper`, { method: 'POST', body: { userId: to } }),
    onSuccess: onDone,
  })
  const others = family.data?.members.filter((x) => x.id !== person.keptBy.id) ?? []
  if (others.length === 0) return null
  return (
    <div className="grid gap-2">
      <Field label="Hand this page to someone else" htmlFor="hand-on">
        <div className="flex gap-2">
          <PersonSelect
            id="hand-on"
            className="flex-1"
            placeholder="Choose someone"
            people={others}
            value={to}
            onChange={setTo}
          />
          <Button variant="outline" disabled={!to || hand.isPending} onClick={() => hand.mutate()}>
            Hand on
          </Button>
        </div>
      </Field>
      <FormError error={hand.error} />
    </div>
  )
}

// "Is this Brenda the same Brenda?" Folding this page into the other one moves everything across.
function SamePerson({ person }: { person: Person }) {
  const tree = useTree(person.familyId)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [into, setInto] = useState('')
  const merge = useMutation({
    mutationFn: () => api(`/people/${person.id}/merge`, { method: 'POST', body: { into } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tree', person.familyId] })
      await queryClient.invalidateQueries({ queryKey: ['person'] })
      navigate(`/person/${into}`, { replace: true })
    },
  })
  // A pet can only be the same as another pet.
  const others = (tree.data?.people ?? []).filter((x) => x.id !== person.id && x.pet === person.pet)
  const target = others.find((x) => x.id === into)
  if (others.length === 0) return null
  return (
    <div className="grid gap-2">
      <Field label="Same person as someone else?" htmlFor="same-person">
        <p className="text-sub text-muted-foreground">
          If {person.name} is on the tree twice, merge this page into the other. Their links, recipes and photos move
          across.
        </p>
        <PersonSelect id="same-person" placeholder="Choose who" people={others} value={into} onChange={setInto} />
      </Field>
      {target && (
        <ConfirmButton
          label={`Merge into ${target.name}`}
          title={`Is ${person.name} the same person as ${target.name}?`}
          description={`This page folds into ${target.name}’s. Everything here moves across, and ${person.name} is no longer listed separately.`}
          confirm="Merge"
          cancel="Keep both"
          disabled={merge.isPending}
          onConfirm={() => merge.mutate()}
        />
      )}
      <FormError error={merge.error} />
    </div>
  )
}

function Remove({ person, first }: { person: Person; first: string }) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const remove = useMutation({
    mutationFn: () => api(`/people/${person.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tree', person.familyId] })
      navigate(`/circle/${person.familyId}/people`, { replace: true })
    },
  })
  return (
    <div className="grid gap-2">
      <FormError error={remove.error} />
      <ConfirmButton
        label="Take off the tree"
        title={`Take ${first} off the tree?`}
        description="Their links go with them. Recipes from them keep their words, just without the link to this page."
        confirm="Take off"
        cancel="Keep"
        disabled={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
    </div>
  )
}

function Unlink({ person, onDone }: { person: Person; onDone: () => void }) {
  const unlink = useMutation({
    mutationFn: () => api(`/people/${person.id}/unlink`, { method: 'POST' }),
    onSuccess: onDone,
  })
  return (
    <section className="flex flex-col gap-2 border-t pt-8">
      <FormError error={unlink.error} />
      <ConfirmButton
        label="This isn’t me"
        title="Unlink your account from this page?"
        description="The page stays on the tree, and you keep looking after it until you hand it on."
        confirm="Unlink"
        cancel="Stay linked"
        disabled={unlink.isPending}
        onConfirm={() => unlink.mutate()}
      />
    </section>
  )
}
